import { Capacitor } from "@capacitor/core";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { API, APIConfig, isAppVersionOutdated, LoginData, newCaregiver } from "../../../../API";
import useOpen from "../../../../hooks/useOpen";
import { allLanguages } from "../../../../language";
import { CaregiverCreateParams, CaregiverMobile } from "../../../../messages";
import { STRINGS } from "../../../../strings";
import { translate } from "../../../../translate";
import { AppButton } from "../../../Generic/AppButton";
import Flex from "../../../Generic/Flex";
import { If } from "../../../Generic/If";
import Spinner from "../../../Generic/Spinner";
import Question from "../../../OnboardingSection/Question";
import useModal from "../../../useModal";
import AddressDialog from "../AddressDialog";
import AuthForm, { AuthFormParagraph, AuthFormTitle } from "../AuthForm";
import CheckboxCards from "../CheckboxCards";
import RadioBoxGroup from "../RadioBoxGroup";

const schema = z.object({
  firstName: z.string().min(1),
  middleName: z.string().optional(),
  lastName: z.string().min(1),
  address: z.string().min(1),
  address2: z.string().optional(),
  addressGeoLocation: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
  gender: z.enum(["M", "F"]),
  languages: z.array(z.string()).min(1),
});

type SchemaData = z.infer<typeof schema>;

interface Props {
  apiConfig: APIConfig;
  loginData: LoginData;
  phonenumber: string | null;
  onSuccess: (params: { caregiver: CaregiverMobile; api: API }) => void;
  onVersionOutdated: () => void;
}

const RegisterStage = (props: Props) => {
  const modal = useModal();
  const addressDialog = useOpen();
  const {
    setValue,
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SchemaData>({
    resolver: zodResolver(schema),
  });

  const handleClickPlace = (place: google.maps.places.PlaceResult) => {
    if (place.geometry === undefined || place.formatted_address === undefined) {
      return modal.setModalAsError({
        title: translate(STRINGS.ERROR),
        message: translate(STRINGS.SOMETHING_WENT_WRONG),
      });
    }
    setValue("addressGeoLocation", {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    });

    setValue("address", place.formatted_address);

    addressDialog.actions.dismiss();
  };

  const newCaregiverMutation = useMutation(
    (params: CaregiverCreateParams) =>
      newCaregiver(
        props.apiConfig,
        props.loginData.tokens.accessJWT,
        props.loginData.tokens.refreshJWT,
        params,
        Capacitor.getPlatform()
      ),
    {}
  );

  const onSubmit = handleSubmit((data) => {
    newCaregiverMutation.mutate(
      {
        address: data.address,
        address2: data.address2,
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        gender: data.gender,
        languages: data.languages,
        phoneNumber: props.phonenumber ?? undefined,
        homePhone: null,
        photoUrl: null,
      },
      {
        onSuccess: (response) => {
          if (isAppVersionOutdated(response)) {
            return props.onVersionOutdated();
          }

          const { caregiver } = response.payload.userLoginDetails;

          if (caregiver === undefined) {
            return modal.setModalAsError({
              title: translate(STRINGS.ERROR),
              message: translate(STRINGS.SOMETHING_WENT_WRONG),
            });
          }

          const api = new API(
            props.apiConfig,
            {
              exp: response.payload.exp,
              accessJWT: response.tokens.accessJWT,
              refreshJWT: response.tokens.refreshJWT,
              caregiverId: caregiver.id,
            },
            Capacitor.getPlatform()
          );

          props.onSuccess({ caregiver, api });
        },
        onError: () => {
          modal.setModalAsError({
            title: translate(STRINGS.ERROR),
            message: translate(STRINGS.SOMETHING_WENT_WRONG),
          });
        },
      }
    );
  });

  return (
    <RegisterStage.Styled onSubmit={onSubmit}>
      <AuthFormTitle>{translate(STRINGS.PLEASE_FILL_IN_YOUR_INFORMATION)}:</AuthFormTitle>

      <RegisterStage.Fields>
        <Flex gap={8} direction="column">
          <Flex gap={8}>
            <Question.Input
              {...register("firstName")}
              placeholder={translate(STRINGS.FIRST_NAME)}
              data-invalid={errors.firstName !== undefined}
            />

            <Question.Input
              {...register("middleName")}
              placeholder={translate(STRINGS.MIDDLE_NAME)}
              data-invalid={errors.middleName !== undefined}
            />
          </Flex>

          <Question.Input
            {...register("lastName")}
            placeholder={translate(STRINGS.LAST_NAME)}
            data-invalid={errors.lastName !== undefined}
          />

          <Question.Input
            {...register("address")}
            placeholder={translate(STRINGS.ADDRESS)}
            onClick={addressDialog.actions.open}
            onFocus={addressDialog.actions.open}
            data-invalid={errors.address !== undefined || errors.addressGeoLocation !== undefined}
          />

          <Question.Input
            {...register("address2")}
            placeholder={translate(STRINGS.ADDRESS2)}
            data-invalid={errors.address2 !== undefined}
          />

          <AddressDialog
            isOpen={addressDialog.isOpen}
            onClickPlace={handleClickPlace}
            onRequestClose={addressDialog.actions.dismiss}
          />

          <RadioBoxGroup>
            <RadioBoxGroup.Item
              {...register("gender")}
              value="F"
              aria-invalid={errors.gender !== undefined}
            >
              {translate(STRINGS.FEMALE)}
            </RadioBoxGroup.Item>

            <RadioBoxGroup.Item
              {...register("gender")}
              value="M"
              aria-invalid={errors.gender !== undefined}
            >
              {translate(STRINGS.MALE)}
            </RadioBoxGroup.Item>
          </RadioBoxGroup>
        </Flex>

        <AuthFormParagraph textAlign="start">{translate(STRINGS.LANGUAGES)}:</AuthFormParagraph>

        <Flex gap={4} wrap="wrap">
          <CheckboxCards
            options={allLanguages}
            control={control}
            aria-invalid={errors.languages !== undefined}
            name="languages"
          />
        </Flex>
      </RegisterStage.Fields>

      <AppButton>
        <If condition={newCaregiverMutation.isLoading}>
          <Spinner />
        </If>
        <span>{translate(STRINGS.NEXT)}</span>
      </AppButton>
    </RegisterStage.Styled>
  );
};

RegisterStage.Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 375px) {
    max-height: 420px;
    overflow-y: auto;
  }
`;

RegisterStage.Styled = styled(AuthForm)`
  ${Question.Input.Styled} {
    padding: 8px 16px;
  }
`;

export default RegisterStage;
